// ============================================================================
// WORK ITEM DRAWER — Slide-over detail panel for a single workqueue item.
//
// Props:
//   item              { referral, workflow } — selected workqueue item
//   onClose           () => void
//   onWorkflowUpdated (referralId, newWorkflow) => void — called after writes
//
// Sections:
//   1. Header (referral ID, specialty, close button)
//   2. Referral summary (outcome badge, pack, createdAt)
//   3. Workflow state  (status + stuck badge, priority, owner)
//   4. Missing Items   (rework requests + touch log — Stage 3)
//   5. Role-gated action panel (intake / triage / scheduler / specialist /
//      tenant_admin — ops_manager is read-only)
//   6. Event history  (append-only workflowEvents)
//
// Zero-PHI invariant: no patient reference, age, or sex displayed.
// ============================================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { safeUserError } from '../core/safeError.js';
import { createPortal } from 'react-dom';
import {
  X, Loader2, AlertCircle, CheckCircle, Clock, User, Flag,
  ClipboardList, Calendar, Eye, CheckCircle2, ArrowRight,
  UserCheck, UserMinus, RefreshCw, History, PackageSearch,
  PhoneCall, AlertTriangle, Paperclip, Undo2, Bell,
} from 'lucide-react';
import {
  WORKFLOW_STATUS, WORKFLOW_PRIORITY, TRIAGE_DECISION,
  getWorkflowState, listWorkflowEvents,
  claimOwner, releaseOwner, updateWorkflowStatus,
  updateWorkflowPriority, setTriageDecision,
  setScheduled, setClosedLoop,
  REWORK_REQUEST_STATUS,
  REWORK_ITEM_TYPE, REWORK_ITEM_TYPE_LABELS,
  REWORK_TOUCH_CHANNEL, REWORK_TOUCH_DIRECTION, REWORK_TOUCH_OUTCOME,
  listReworkRequests, listReworkTouches,
  createReworkRequest, logReworkTouch,
  RETURN_REASON_CODES, RETURN_REASON_LABELS,
  getTenantUser, getTenantUsers,
  createCommunicationPing, COMMUNICATION_PURPOSE, COMMUNICATION_PURPOSE_LABELS, RELATED_OBJECT_TYPE,
  PURPOSE_RECEIVER_ROLES,
} from '../core/index.js';
import {
  ROLE_DISPLAY,
  REFERRAL_PINGS_ENABLED_ROLES,
  canCreateReferralPing,
} from '../core/rbac.js';
import { returnToIntake } from '../core/store.scheduling.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { PHI_MODE } from '../config/featureFlags.js';
import BookAppointmentModal from './BookAppointmentModal.jsx';
import MessagingCard from './Gate/MessagingCard.jsx';
import PingComposer from './PingComposer.jsx';
import {
  getPatientIdentity,
  getContact,
  getInsurance,
} from '../core/phiApi.js';
import {
  createAttachmentUploadUrl, finalizeAttachmentUpload,
  listAttachments,
} from '../core/phiAttachmentsApi.js';
import { navigateToAttachmentViewer } from '../components/AttachmentPdfViewer.jsx';
import { getApp } from 'firebase/app';
import { getFirestore, doc, getDoc, collection, onSnapshot } from 'firebase/firestore';

// ── Constants ─────────────────────────────────────────────────────────────────

const STUCK_DAYS = 3;

// PHI role gates (union of canAccessPhi() results from phiPolicy.js)
// PHI_READ_ROLES  = union of all roles that can READ any doc type
const PHI_READ_ROLES  = ['tenant_admin', 'intake_coordinator', 'triage_nurse', 'scheduler', 'specialist'];
// Roles that can READ insurance (mirrors phiPolicy.js — scheduler is excluded)
const PHI_INSURANCE_READ_ROLES = ['tenant_admin', 'intake_coordinator', 'specialist', 'specialist_admin'];
// Roles that can EDIT patient identity and contact fields in the drawer
// (intake_coordinator + scheduler = read-only viewers)
const PHI_IDENTITY_EDIT_ROLES = ['tenant_admin'];
const PHI_CONTACT_EDIT_ROLES  = ['tenant_admin'];
// Attachment role gates (mirror phiPolicy.js — Step 3)
const PHI_UPLOAD_ROLES   = ['tenant_admin', 'intake_coordinator'];
const PHI_DOWNLOAD_ROLES = ['tenant_admin', 'intake_coordinator', 'triage_nurse', 'scheduler', 'specialist'];

// ── Display config lookup tables ─────────────────────────────────────────────

const STATUS_CONFIG = {
  [WORKFLOW_STATUS.NEW]:               { label: 'New',               tw: 'bg-blue-500/15 text-blue-300 border-blue-500/20' },
  [WORKFLOW_STATUS.NEEDS_INFO]:        { label: 'Needs Info',        tw: 'bg-amber-500/15 text-amber-300 border-amber-500/20' },
  [WORKFLOW_STATUS.READY_TO_SCHEDULE]: { label: 'Ready to Schedule', tw: 'bg-violet-500/15 text-violet-300 border-violet-500/20' },
  [WORKFLOW_STATUS.SCHEDULED]:         { label: 'Scheduled',         tw: 'bg-teal-500/15 text-teal-300 border-teal-500/20' },
  [WORKFLOW_STATUS.SEEN]:              { label: 'Seen',              tw: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/20' },
  [WORKFLOW_STATUS.CLOSED_LOOP]:       { label: 'Closed Loop',       tw: 'bg-slate-500/15 text-slate-300 border-slate-500/20' },
  [WORKFLOW_STATUS.CANCELLED]:         { label: 'Cancelled',         tw: 'bg-red-500/15 text-red-300 border-red-500/20' },
  [WORKFLOW_STATUS.NO_SHOW]:           { label: 'No-Show',           tw: 'bg-orange-500/15 text-orange-300 border-orange-500/20' },
};

const PRIORITY_CONFIG = {
  [WORKFLOW_PRIORITY.NORMAL]: { label: 'Normal', tw: 'bg-slate-700/60 text-slate-400 border-slate-600/30' },
  [WORKFLOW_PRIORITY.HIGH]:   { label: 'High',   tw: 'bg-orange-500/15 text-orange-300 border-orange-500/20' },
  [WORKFLOW_PRIORITY.URGENT]: { label: 'Urgent', tw: 'bg-red-500/15 text-red-300 border-red-500/20' },
};

const TRIAGE_CONFIG = {
  [TRIAGE_DECISION.NONE]:          { label: 'Pending Triage',  tw: 'bg-slate-700/60 text-slate-400 border-slate-600/30' },
  [TRIAGE_DECISION.OK]:            { label: 'OK',              tw: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/20' },
  [TRIAGE_DECISION.URGENT_REVIEW]: { label: 'Urgent Review',   tw: 'bg-red-500/15 text-red-300 border-red-500/20' },
  [TRIAGE_DECISION.REDIRECT]:      { label: 'Redirect',        tw: 'bg-amber-500/15 text-amber-300 border-amber-500/20' },
  _COMPLETED:                      { label: 'Triage Completed', tw: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/20' },
};

// Keys match outcome.status as written by buildReferralDoc (lowercase).
// Source of truth: tenants/{tenantId}/referrals/{referralId}.outcome.status
const OUTCOME_CONFIG = {
  routine: { label: 'Routine', tw: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/20' },
  urgent:  { label: 'Urgent',  tw: 'bg-red-500/15 text-red-300 border-red-500/20' },
  not_yet: { label: 'Not Yet', tw: 'bg-amber-500/15 text-amber-300 border-amber-500/20' },
};

// Rework request status display config (schema v2 — one flat doc per item type)
const REWORK_STATUS_CONFIG = {
  [REWORK_REQUEST_STATUS.OPEN]:     { label: 'Open',     tw: 'bg-amber-500/15 text-amber-300 border-amber-500/20' },
  [REWORK_REQUEST_STATUS.RECEIVED]: { label: 'Received', tw: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/20' },
  [REWORK_REQUEST_STATUS.RESOLVED]: { label: 'Resolved', tw: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/20' },
};

// Legacy per-item status labels (REWORK_ITEM_STATUS in store.rework.js — kept
// only for rendering REWORK_ITEM_STATUS_CHANGED events in the history timeline).
// Referenced below with `?.label ?? rawStatus`, so any unmapped value degrades
// gracefully to the raw status string. Previously this map was missing entirely,
// which threw "REWORK_ITEM_STATUS_CONFIG is not defined" whenever such an event
// rendered.
const REWORK_ITEM_STATUS_CONFIG = {
  MISSING:   { label: 'Missing' },
  REQUESTED: { label: 'Requested' },
  PROVIDED:  { label: 'Provided' },
  RECEIVED:  { label: 'Received' },
};

const TOUCH_CHANNEL_LABELS = {
  [REWORK_TOUCH_CHANNEL.PHONE]:     'Phone',
  [REWORK_TOUCH_CHANNEL.FAX]:       'Fax',
  [REWORK_TOUCH_CHANNEL.PORTAL]:    'Portal',
  [REWORK_TOUCH_CHANNEL.IN_PERSON]: 'In Person',
  [REWORK_TOUCH_CHANNEL.OTHER]:     'Other',
};

const TOUCH_DIRECTION_LABELS = {
  [REWORK_TOUCH_DIRECTION.OUTBOUND]: 'Outbound',
  [REWORK_TOUCH_DIRECTION.INBOUND]:  'Inbound',
};

const TOUCH_OUTCOME_LABELS = {
  [REWORK_TOUCH_OUTCOME.NO_ANSWER]:    'No Answer',
  [REWORK_TOUCH_OUTCOME.LEFT_MESSAGE]: 'Left Message',
  [REWORK_TOUCH_OUTCOME.SENT_REQUEST]: 'Sent Request',
  [REWORK_TOUCH_OUTCOME.RECEIVED_INFO]:'Received Info',
  [REWORK_TOUCH_OUTCOME.OTHER]:        'Other',
};

const EVENT_LABELS = {
  WORKFLOW_CREATED:           'Referral received',
  OWNER_ASSIGNED:             'Owner assigned',
  OWNER_UNASSIGNED:           'Owner released',
  STATUS_CHANGED:             'Status changed',
  TRIAGE_UPDATED:             'Triage updated',
  SCHEDULED_SET:              'Appointment scheduled',
  SEEN_SET:                   'Patient seen',
  CLOSED_LOOP_SET:            'Closed loop',
  NO_SHOW_SET:                'Marked no-show',
  PRIORITY_CHANGED:           'Priority updated',
  REWORK_REQUEST_CREATED:     'Missing items request created',
  REWORK_ITEM_STATUS_CHANGED: 'Item status updated',
  REWORK_TOUCH_LOGGED:        'Outreach touch logged',
  REWORK_RESOLVED:            'Missing items resolved',
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtTs(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : (ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts));
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function fmtDate(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : (ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts));
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function shortId(id) {
  if (!id) return '—';
  return id.length > 12 ? `…${id.slice(-8)}` : id;
}

/**
 * Resolves a member UID to their display name via tenants/{tenantId}/members/{uid}.
 * Falls back to a shortened UID if the lookup fails or is loading.
 */
function emailToName(email) {
  if (!email) return null;
  const local = email.split('@')[0] ?? '';
  return local.replace(/[._-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) || null;
}

function MemberName({ uid, showRole = false }) {
  const [label, setLabel] = React.useState(null);
  const [roleName, setRoleName] = React.useState(null);
  React.useEffect(() => {
    if (!uid) return;
    getDoc(doc(getFirestore(getApp()), `users/${uid}`))
      .then((snap) => {
        if (!snap.exists()) return;
        const d = snap.data();
        setLabel(d.displayName || d.name || emailToName(d.email) || null);
        if (showRole && d.role) {
          setRoleName(ROLE_DISPLAY[d.role]?.name ?? d.role);
        }
      })
      .catch(() => {});
  }, [uid, showRole]);
  if (!uid) return <span className="text-slate-500">—</span>;
  return (
    <span className="text-slate-300 text-xs">
      {label ?? shortId(uid)}{showRole && roleName ? ` (${roleName})` : ''}
    </span>
  );
}

/**
 * Canonical override check.
 * Primary source: referral doc — outcome.isOverride / override.used (Gate-authored).
 * Fallback: workflow/state — overrideDecision / overriddenBy (triage-authored).
 */
function isOverridden(referral, workflow) {
  if (referral?.outcome?.isOverride === true) return true;
  if (referral?.override?.used === true)      return true;
  return !!(
    workflow?.overrideDecision ||
    workflow?.overrideReason   ||
    workflow?.overriddenBy     ||
    workflow?.overriddenAt
  );
}

/** Extract override detail fields from referral + workflow, normalising both sources. */
function getOverrideDetail(referral, workflow) {
  // Gate-authored override lives on the referral doc
  const gateReason = referral?.outcome?.overrideReason ?? referral?.override?.reason  ?? null;
  const gateNote   = referral?.override?.note          ?? null;
  const gateBy     = referral?.outcome?.overrideByUid  ?? referral?.override?.confirmedByUid ?? null;
  const gateAt     = referral?.outcome?.overrideAt     ?? referral?.override?.confirmedAt    ?? null;
  // Triage-authored override lives on workflow/state
  const triageReason = workflow?.overrideReason   ?? null;
  const triageNote   = workflow?.overrideNote      ?? null;
  const triageBy     = workflow?.overriddenBy      ?? null;
  const triageAt     = workflow?.overriddenAt      ?? null;
  return {
    reason: gateReason   ?? triageReason,
    note:   gateNote     ?? triageNote,
    by:     gateBy       ?? triageBy,
    at:     gateAt       ?? triageAt,
  };
}

function computeStuck(wf) {
  if (wf?.status !== WORKFLOW_STATUS.NEEDS_INFO) return false;
  if (!(wf?.rework?.missingItemsCount > 0)) return false;
  const lt = wf?.rework?.lastTouchAt;
  if (!lt) return true;
  const d = lt.toDate ? lt.toDate() : new Date(lt.seconds * 1000);
  return (Date.now() - d.getTime()) > STUCK_DAYS * 86400_000;
}

// ── Sub-components ───────────────────────────────────────────────────────────

function Badge({ tw, label }) {
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${tw}`}>
      {label}
    </span>
  );
}

function InfoRow({ label, children }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5">
      <span className="text-xs text-slate-500 flex-shrink-0 w-28">{label}</span>
      <div className="text-xs text-slate-300 text-right">{children}</div>
    </div>
  );
}

function SectionTitle({ children }) {
  return <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">{children}</h3>;
}

function ActionError({ msg, onRetry }) {
  if (!msg) return null;
  return (
    <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 mt-3">
      <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
      <div>
        <p className="text-xs text-red-400">{msg}</p>
        {onRetry && (
          <button
            onClick={onRetry}
            className="text-xs text-red-300 underline mt-1 hover:text-red-200 transition-colors"
          >
            Refresh &amp; retry
          </button>
        )}
      </div>
    </div>
  );
}

// ── Toast ────────────────────────────────────────────────────────────────────

function Toast({ message, type, onClose }) {
  return (
    <div className={`fixed bottom-6 right-6 z-[60] flex items-center gap-3 px-5 py-3 rounded-2xl shadow-2xl border
      ${type === 'success'
        ? 'bg-emerald-500/20 border-emerald-500/30 text-emerald-300'
        : 'bg-red-500/20 border-red-500/30 text-red-300'}`}
    >
      {type === 'success'
        ? <CheckCircle className="w-4 h-4" />
        : <AlertCircle className="w-4 h-4" />}
      <span className="text-sm font-medium">{message}</span>
      <button onClick={onClose} className="ml-2 opacity-60 hover:opacity-100">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

// ── Create Request Modal ──────────────────────────────────────────────────────

function CreateRequestModal({ onClose, onSubmit, submitting }) {
  const [selected, setSelected] = useState([]);
  const [error, setError] = useState('');

  const ALL_TYPES = Object.values(REWORK_ITEM_TYPE);

  const toggle = (type) => {
    setSelected((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleSubmit = () => {
    if (selected.length === 0) {
      setError('Select at least one item type.');
      return;
    }
    setError('');
    onSubmit(selected);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-sm bg-slate-900 border border-slate-700/60 rounded-2xl shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700/50">
          <h2 className="text-sm font-semibold text-white">Request Missing Items</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {/* PHI warning */}
          <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
            <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-300">
              No PHI — do not enter patient identifiers. Select item types only.
            </p>
          </div>

          {/* Checkboxes */}
          <div>
            <p className="text-xs font-medium text-slate-400 mb-2">Missing item types</p>
            <div className="space-y-2">
              {ALL_TYPES.map((type) => (
                <label
                  key={type}
                  className="flex items-center gap-3 cursor-pointer group"
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(type)}
                    onChange={() => toggle(type)}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-violet-500 focus:ring-violet-500/30 cursor-pointer"
                  />
                  <span className="text-sm text-slate-300 group-hover:text-white transition-colors">
                    {REWORK_ITEM_TYPE_LABELS[type] ?? type}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {error && <p className="text-xs text-red-400">{error}</p>}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-slate-700/50 flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-xl text-sm text-slate-400 hover:text-white hover:bg-slate-800 disabled:opacity-50 transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || selected.length === 0}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-600/30 hover:bg-amber-500/40 text-amber-300 text-sm font-medium disabled:opacity-50 transition-all"
          >
            {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Create Request
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Log Touch Modal ───────────────────────────────────────────────────────────

function LogTouchModal({ onClose, onSubmit, submitting }) {
  const [channel, setChannel]     = useState(REWORK_TOUCH_CHANNEL.PHONE);
  const [direction, setDirection] = useState(REWORK_TOUCH_DIRECTION.OUTBOUND);
  const [outcome, setOutcome]     = useState(REWORK_TOUCH_OUTCOME.LEFT_MESSAGE);

  const selectCls = 'w-full px-3 py-2 rounded-xl bg-slate-800 border border-slate-700/50 text-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-500/30';

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />

      <div className="relative w-full max-w-sm bg-slate-900 border border-slate-700/60 rounded-2xl shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700/50">
          <h2 className="text-sm font-semibold text-white">Log Outreach Touch</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5">Channel</label>
            <select value={channel} onChange={(e) => setChannel(e.target.value)} className={selectCls}>
              {Object.values(REWORK_TOUCH_CHANNEL).map((c) => (
                <option key={c} value={c}>{TOUCH_CHANNEL_LABELS[c] ?? c}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5">Direction</label>
            <select value={direction} onChange={(e) => setDirection(e.target.value)} className={selectCls}>
              {Object.values(REWORK_TOUCH_DIRECTION).map((d) => (
                <option key={d} value={d}>{TOUCH_DIRECTION_LABELS[d] ?? d}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5">Outcome</label>
            <select value={outcome} onChange={(e) => setOutcome(e.target.value)} className={selectCls}>
              {Object.values(REWORK_TOUCH_OUTCOME).map((o) => (
                <option key={o} value={o}>{TOUCH_OUTCOME_LABELS[o] ?? o}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-slate-700/50 flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-xl text-sm text-slate-400 hover:text-white hover:bg-slate-800 disabled:opacity-50 transition-all"
          >
            Cancel
          </button>
          <button
            onClick={() => onSubmit(channel, direction, outcome)}
            disabled={submitting}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-teal-600/30 hover:bg-teal-500/40 text-teal-300 text-sm font-medium disabled:opacity-50 transition-all"
          >
            {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Save Touch
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Missing Items Section ─────────────────────────────────────────────────────
//
// Schema v2: each reworkRequest doc is a flat record for one item type.
//   { id, itemType, status: 'open'|'received', createdAt, resolvedAt, ... }
//
// Source of truth for RECEIVED: system automation (attachment READY → CF).
// Intake does NOT manually mark items received.

function MissingItemsSection({
  reworkRequests, reworkTouches, reworkLoading, reworkError,
}) {
  if (reworkLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="w-5 h-5 text-slate-500 animate-spin" />
      </div>
    );
  }

  if (reworkError) {
    return (
      <div className="flex items-start gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20">
        <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-red-400">{reworkError}</p>
      </div>
    );
  }

  const hasTouches  = reworkTouches.length > 0;
  const hasRequests = reworkRequests.length > 0;

  if (!hasRequests && !hasTouches) {
    return (
      <p className="text-xs text-slate-500 italic text-center py-3">
        No missing items requested yet.
      </p>
    );
  }

  // Flat sort: OPEN first (oldest first), RECEIVED second (oldest first)
  // Handles both schema v2 (flat doc, req.status) and v1 legacy docs
  const isOpen = (r) => {
    // v2 flat doc
    if (r.status === REWORK_REQUEST_STATUS.OPEN) return true;
    // v1 doc with items[] — open if any item is not RECEIVED
    if (Array.isArray(r.items)) {
      return r.items.some((it) => it.itemStatus !== 'RECEIVED');
    }
    return false;
  };

  const epoch = (ts) => {
    if (!ts) return 0;
    return ts.seconds ?? (ts.toMillis ? ts.toMillis() / 1000 : 0);
  };

  const sorted = [...reworkRequests].sort((a, b) => {
    const aOpen = isOpen(a) ? 0 : 1;
    const bOpen = isOpen(b) ? 0 : 1;
    if (aOpen !== bOpen) return aOpen - bOpen;
    return epoch(a.createdAt) - epoch(b.createdAt); // oldest first within group
  });

  return (
    <div className="space-y-4">
      {/* Request rows */}
      {hasRequests && (
        <div className="space-y-2">
          {sorted.map((req) => {
            // v2 flat doc
            const isV2     = req.schemaVersion === 2 || (req.itemType && !req.items);
            const open     = isOpen(req);
            const cfg      = REWORK_STATUS_CONFIG[req.status] ?? REWORK_STATUS_CONFIG[REWORK_REQUEST_STATUS.OPEN];

            if (isV2) {
              const label = REWORK_ITEM_TYPE_LABELS[req.itemType] ?? req.itemType;
              return (
                <div
                  key={req.id}
                  className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl bg-slate-800/50 border border-slate-700/30"
                >
                  <div className="min-w-0">
                    <p className={`text-xs font-medium ${open ? 'text-white' : 'text-slate-400'}`}>
                      {label}
                    </p>
                    <p className="text-[11px] text-slate-600 mt-0.5">
                      {open
                        ? req.createdAt ? `Requested ${fmtDate(req.createdAt)}` : 'Requested'
                        : req.resolvedAt ? `Received ${fmtDate(req.resolvedAt)}` : 'Received'}
                    </p>
                  </div>
                  <Badge tw={cfg.tw} label={cfg.label} />
                </div>
              );
            }

            // v1 legacy: render items[] array (read-only, no action buttons)
            return (
              <div
                key={req.id}
                className="bg-slate-800/50 rounded-xl border border-slate-700/30 overflow-hidden"
              >
                <div className="px-3 py-1.5 border-b border-slate-700/30 flex items-center justify-between">
                  <span className="text-[11px] text-slate-500 font-mono">{shortId(req.id)}</span>
                  <span className="text-[11px] text-slate-600">{fmtTs(req.createdAt)}</span>
                </div>
                <div className="divide-y divide-slate-700/20">
                  {(req.items ?? []).map((item) => {
                    const itemOpen = item.itemStatus !== 'RECEIVED' && item.itemStatus !== 'PROVIDED';
                    return (
                      <div key={item.itemType} className="flex items-center justify-between gap-3 px-3 py-2">
                        <span className="text-xs text-slate-300">
                          {REWORK_ITEM_TYPE_LABELS[item.itemType] ?? item.itemType}
                        </span>
                        <Badge
                          tw={itemOpen
                            ? 'bg-amber-500/15 text-amber-300 border-amber-500/20'
                            : 'bg-emerald-500/15 text-emerald-300 border-emerald-500/20'}
                          label={itemOpen ? 'Open' : 'Received'}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Touch history */}
      {hasTouches && (
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Outreach Log</p>
          <div className="space-y-1">
            {reworkTouches.map((t) => (
              <div key={t.id} className="flex items-center gap-2 py-1.5 border-b border-slate-800/60 last:border-0">
                <PhoneCall className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                <span className="text-xs text-slate-300">
                  {TOUCH_CHANNEL_LABELS[t.channel] ?? t.channel}
                  {' · '}
                  {TOUCH_DIRECTION_LABELS[t.direction] ?? t.direction}
                  {' · '}
                  {TOUCH_OUTCOME_LABELS[t.outcome] ?? t.outcome}
                </span>
                <span className="text-xs text-slate-600 ml-auto flex-shrink-0">{fmtTs(t.createdAt)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Role-gated action panels ─────────────────────────────────────────────────

function IntakePanel({
  workflow, tenantId, referralId, uid, referral, reworkRequests = [],
  onAction, actionLoading, actionError, onAfterAction,
  onShowCreateModal, onShowTouchModal, activeFocusTarget,
}) {
  const isOwner = workflow?.ownerUid === uid;
  const [showClaimHint, setShowClaimHint] = useState(false);

  // Intake coordinators must claim a referral before changing its status or
  // requesting missing items. If not the owner, surface a hint and pulse the
  // Claim button instead of attempting the (rule-denied) action.
  const requireOwner = (proceed) => {
    if (!isOwner) {
      setShowClaimHint(true);
      return;
    }
    proceed();
  };

  const STATUS_OPTIONS = [
    WORKFLOW_STATUS.NEW,
    WORKFLOW_STATUS.NEEDS_INFO,
    WORKFLOW_STATUS.READY_TO_SCHEDULE,
  ];

  return (
    <div className="space-y-4">
      {/* Ownership */}
      <div>
        <p className="text-xs font-medium text-slate-400 mb-2">Ownership</p>
        <div className="flex gap-2">
          {!isOwner ? (
            <button
              onClick={() => { setShowClaimHint(false); onAction('claim', () => claimOwner(tenantId, referralId, workflow, uid, 'intake_coordinator')); }}
              disabled={!!actionLoading}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl bg-teal-600/20 hover:bg-teal-500/30 text-teal-400 text-xs font-medium disabled:opacity-50 transition-all${showClaimHint ? ' ring-2 ring-amber-400 animate-pulse' : ''}`}
              data-learning-target="claim-button"
            >
              {actionLoading === 'claim' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserCheck className="w-3.5 h-3.5" />}
              Claim
            </button>
          ) : (
            <button
              onClick={() => onAction('release', () => releaseOwner(tenantId, referralId, workflow, uid, 'intake_coordinator'))}
              disabled={!!actionLoading}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-400 text-xs font-medium disabled:opacity-50 transition-all"
            >
              {actionLoading === 'release' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserMinus className="w-3.5 h-3.5" />}
              Release
            </button>
          )}
        </div>
        {showClaimHint && !isOwner && (
          <p className="mt-2 text-xs text-amber-400">
            Claim this referral first to change its status or request missing items.
          </p>
        )}
      </div>

      {/* Status */}
      <div>
        <p className="text-xs font-medium text-slate-400 mb-2">Set Status</p>
        <div className="flex flex-wrap gap-2">
          {STATUS_OPTIONS.map((s) => {
            const cfg = STATUS_CONFIG[s];
            const isCurrent = workflow?.status === s;
            const learningTarget = s === WORKFLOW_STATUS.NEEDS_INFO ? 'needs-info-status-button'
              : s === WORKFLOW_STATUS.READY_TO_SCHEDULE ? 'ready-to-schedule-button'
              : undefined;
            return (
              <button
                key={s}
                onClick={() => requireOwner(() => onAction(`status_${s}`, () => updateWorkflowStatus(tenantId, referralId, workflow, s, uid, 'intake_coordinator')))}
                disabled={!!actionLoading || isCurrent}
                className={`px-3 py-1.5 rounded-xl text-xs font-medium disabled:opacity-50 transition-all border
                  ${isCurrent ? cfg.tw + ' cursor-default' : 'bg-slate-800 border-slate-700/50 text-slate-400 hover:bg-slate-700 hover:text-white'}
                  ${learningTarget && activeFocusTarget === learningTarget ? ' learning-focus-highlight-subtle' : ''}`}
                {...(learningTarget ? { 'data-learning-target': learningTarget } : {})}
              >
                {actionLoading === `status_${s}` && <Loader2 className="w-3 h-3 animate-spin inline mr-1" />}
                {cfg?.label ?? s}
              </button>
            );
          })}
        </div>
      </div>

      {/* Rework actions */}
      <div>
        <p className="text-xs font-medium text-slate-400 mb-2">Rework</p>
        <div className="flex gap-2">
          <button
            onClick={() => requireOwner(onShowCreateModal)}
            disabled={!!actionLoading}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl bg-amber-600/20 hover:bg-amber-500/30 text-amber-400 text-xs font-medium disabled:opacity-50 transition-all${activeFocusTarget === 'request-missing-items-button' ? ' learning-focus-highlight-subtle' : ''}`}
            data-learning-target="request-missing-items-button"
          >
            <PackageSearch className="w-3.5 h-3.5" />
            Request Missing Items
          </button>
          <button
            onClick={onShowTouchModal}
            disabled={!!actionLoading}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl bg-teal-600/20 hover:bg-teal-500/30 text-teal-400 text-xs font-medium disabled:opacity-50 transition-all${activeFocusTarget === 'log-touch-button' ? ' learning-focus-highlight-subtle' : ''}`}
            data-learning-target="log-touch-button"
          >
            <PhoneCall className="w-3.5 h-3.5" />
            Log Touch
          </button>
        </div>
      </div>

      <ActionError msg={actionError} onRetry={onAfterAction} />
    </div>
  );
}

function TriagePanel({
  workflow, tenantId, referralId, uid,
  onAction, actionLoading, actionError, onAfterAction,
  onShowCreateModal, onShowTouchModal,
}) {
  const decisions = [
    { val: TRIAGE_DECISION.OK,            label: 'OK',            toStatus: WORKFLOW_STATUS.READY_TO_SCHEDULE },
    { val: TRIAGE_DECISION.URGENT_REVIEW, label: 'Urgent Review', toStatus: WORKFLOW_STATUS.NEEDS_INFO },
    { val: TRIAGE_DECISION.REDIRECT,      label: 'Redirect',      toStatus: WORKFLOW_STATUS.NEEDS_INFO },
  ];

  return (
    <div className="space-y-4">
      {/* Triage Decision */}
      <div>
        <p className="text-xs font-medium text-slate-400 mb-2">Triage Decision</p>
        <div className="flex flex-wrap gap-2">
          {decisions.map(({ val, label, toStatus }) => {
            const cfg = TRIAGE_CONFIG[val];
            const isCurrent = workflow?.triage?.triageDecision === val;
            return (
              <button
                key={val}
                onClick={() => onAction(`triage_${val}`, () => setTriageDecision(tenantId, referralId, workflow, val, uid, toStatus))}
                disabled={!!actionLoading || isCurrent}
                className={`px-3 py-1.5 rounded-xl text-xs font-medium disabled:opacity-50 transition-all border
                  ${isCurrent ? cfg.tw + ' cursor-default' : 'bg-slate-800 border-slate-700/50 text-slate-400 hover:bg-slate-700 hover:text-white'}`}
              >
                {actionLoading === `triage_${val}` && <Loader2 className="w-3 h-3 animate-spin inline mr-1" />}
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Rework actions */}
      <div>
        <p className="text-xs font-medium text-slate-400 mb-2">Rework</p>
        <div className="flex gap-2">
          <button
            onClick={onShowCreateModal}
            disabled={!!actionLoading}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-amber-600/20 hover:bg-amber-500/30 text-amber-400 text-xs font-medium disabled:opacity-50 transition-all"
          >
            <PackageSearch className="w-3.5 h-3.5" />
            Request Missing Items
          </button>
          <button
            onClick={onShowTouchModal}
            disabled={!!actionLoading}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-teal-600/20 hover:bg-teal-500/30 text-teal-400 text-xs font-medium disabled:opacity-50 transition-all"
          >
            <PhoneCall className="w-3.5 h-3.5" />
            Log Touch
          </button>
        </div>
      </div>

      <ActionError msg={actionError} onRetry={onAfterAction} />
    </div>
  );
}

// ── Confirm Modal (generic, reusable) ────────────────────────────────────────

function ConfirmModal({ title, description, onClose, onConfirm, submitting, confirmLabel = 'Confirm', confirmTw = 'bg-indigo-600/30 hover:bg-indigo-500/40 text-indigo-300' }) {
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={!submitting ? onClose : undefined} />
      <div className="relative w-full max-w-sm bg-slate-900 border border-slate-700/60 rounded-2xl shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700/50">
          <h2 className="text-sm font-semibold text-white">{title}</h2>
          <button onClick={onClose} disabled={submitting} className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors disabled:opacity-40">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-5 py-4">
          <p className="text-sm text-slate-300">{description}</p>
        </div>
        <div className="px-5 py-4 border-t border-slate-700/50 flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-xl text-sm text-slate-400 hover:text-white hover:bg-slate-800 disabled:opacity-50 transition-all"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={submitting}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium disabled:opacity-50 transition-all ${confirmTw}`}
          >
            {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Scheduler Panel ───────────────────────────────────────────────────────────

function SchedulerPanel({ workflow, tenantId, referralId, referral, uid, role, onAction, actionLoading, actionError, onAfterAction, footerEl, activeFocusTarget }) {
  const [showConfirmScheduled, setShowConfirmScheduled] = useState(false);
  const [showConfirmSeen, setShowConfirmSeen]           = useState(false);
  const [showReturnModal, setShowReturnModal]           = useState(false);
  const [modalSubmitting, setModalSubmitting]           = useState(false);

  // Return-to-intake form state
  const [returnReason, setReturnReason]       = useState('');
  const [returnItems, setReturnItems]         = useState([]);
  const [returnNote, setReturnNote]           = useState('');
  const [returnReceiver, setReturnReceiver]   = useState('');
  const [intakeUsers, setIntakeUsers]         = useState([]);
  const [intakeLoading, setIntakeLoading]     = useState(false);
  const [intakeError, setIntakeError]         = useState(null);
  const [myName, setMyName]                   = useState(null);
  const [returnError, setReturnError]         = useState(null);

  // Load active Intake Coordinators (receiver options) when the return modal opens.
  useEffect(() => {
    if (!showReturnModal || !tenantId) return;
    let cancelled = false;
    setIntakeLoading(true);
    setIntakeError(null);
    getTenantUsers(tenantId)
      .then((list) => {
        if (cancelled) return;
        const arr = list || [];
        const me = arr.find((u) => (u.uid || u.id) === uid);
        setMyName(me?.displayName || me?.name || null);
        const coords = arr.filter((u) => {
          const ru = u.uid || u.id;
          if (!ru || ru === uid) return false;
          if (u.role !== 'intake_coordinator') return false;
          return u.isActive !== false && u.status !== 'disabled';
        });
        setIntakeUsers(coords);
        // Preselect prior intake owner if they're a valid active coordinator.
        const prior = workflow?.ownerUid ?? workflow?.enteredOpsBy ?? null;
        if (prior && coords.some((u) => (u.uid || u.id) === prior)) setReturnReceiver(prior);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[SchedulerPanel] getTenantUsers failed:', err?.message || err);
        setIntakeError('Could not load tenant users.');
      })
      .finally(() => { if (!cancelled) setIntakeLoading(false); });
    return () => { cancelled = true; };
  }, [showReturnModal, tenantId, uid]); // eslint-disable-line react-hooks/exhaustive-deps

  const status        = workflow?.status;
  const scheduledAt   = workflow?.scheduling?.scheduledAt;
  const seenAt        = workflow?.scheduling?.seenAt;
  const isReadyToSch  = status === WORKFLOW_STATUS.READY_TO_SCHEDULE;
  const isScheduled   = status === WORKFLOW_STATUS.SCHEDULED;
  const isSeen        = status === WORKFLOW_STATUS.SEEN;
  const canSchedule   = isReadyToSch;
  const canSee        = isScheduled && !!scheduledAt;

  const doSeen = async () => {
    setModalSubmitting(true);
    try {
      await new Promise((resolve, reject) =>
        onAction('seen', () => updateWorkflowStatus(tenantId, referralId, workflow, WORKFLOW_STATUS.SEEN, uid, role))
          .then(resolve).catch(reject)
      );
      setShowConfirmSeen(false);
    } catch (_) {
      // error already handled by onAction → toast
    } finally {
      setModalSubmitting(false);
    }
  };

  const doReturn = async () => {
    if (!returnReason) { setReturnError('Add a return reason before sending this referral back to Intake.'); return; }
    if (!returnReceiver) { setReturnError('Select an Intake Coordinator before returning this referral.'); return; }
    setModalSubmitting(true);
    setReturnError(null);
    try {
      await returnToIntake(tenantId, referralId, {
        reasonCode: returnReason,
        selectedItemTypes: returnItems,
        note: returnNote.trim(),
        receiverUid: returnReceiver,
        receiverRole: 'intake_coordinator',
        returnedByName: myName,
      }, uid, role);
      setShowReturnModal(false);
      setReturnReason('');
      setReturnItems([]);
      setReturnNote('');
      setReturnReceiver('');
      if (onAfterAction) onAfterAction();
    } catch (err) {
      console.error('[SchedulerPanel] returnToIntake failed:', err?.message || err);
      setReturnError('Could not return this referral to Intake. Nothing was changed.');
    } finally {
      setModalSubmitting(false);
    }
  };

  const toggleReturnItem = (type) => {
    setReturnItems((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const canSubmitReturn = !!returnReason && returnItems.length > 0 && !!returnReceiver && !intakeLoading && !intakeError;

  return (
    <>
      <div className="space-y-4">

        {/* READY_TO_SCHEDULE → status info */}
        {(canSchedule || (!isScheduled && !isSeen)) && (
          <div>
            {!canSchedule && !isSeen && !isScheduled && (
              <p className="text-xs text-slate-500">
                Referral must be in Ready to Schedule status.
              </p>
            )}
          </div>
        )}

        {/* SCHEDULED → show scheduledAt */}
        {isScheduled && (
          <div className="bg-slate-800/60 rounded-xl px-4 py-3 border border-slate-700/40">
            <p className="text-xs text-slate-500 mb-0.5">Scheduled at</p>
            <p className="text-sm font-medium text-slate-200">{fmtTs(scheduledAt)}</p>
          </div>
        )}

        {/* SEEN → read-only summary */}
        {isSeen && (
          <div className="space-y-2">
            <div className="bg-slate-800/60 rounded-xl px-4 py-3 border border-slate-700/40 space-y-2">
              <div>
                <p className="text-xs text-slate-500">Scheduled at</p>
                <p className="text-sm font-medium text-slate-200">{fmtTs(scheduledAt)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Seen at</p>
                <p className="text-sm font-medium text-emerald-300">{fmtTs(seenAt)}</p>
              </div>
            </div>
            <p className="text-xs text-slate-600 italic text-center">
              Appointment completed — closed loop coming next.
            </p>
          </div>
        )}

        <ActionError msg={actionError} onRetry={onAfterAction} />
      </div>

      {/* Confirmation modals */}
      {showConfirmScheduled && (
        <BookAppointmentModal
          item={{ referral, workflow }}
          uid={uid}
          role={role}
          onClose={() => setShowConfirmScheduled(false)}
          onBooked={() => {
            setShowConfirmScheduled(false);
            if (onAfterAction) onAfterAction();
          }}
        />
      )}
      {showConfirmSeen && (
        <ConfirmModal
          title="Confirm Seen"
          description="Confirm that the patient attended their appointment. This will move the referral to Seen status."
          confirmLabel="Confirm Seen"
          confirmTw="bg-emerald-600/30 hover:bg-emerald-500/40 text-emerald-300"
          onClose={() => !modalSubmitting && setShowConfirmSeen(false)}
          onConfirm={doSeen}
          submitting={modalSubmitting}
        />
      )}

      {/* Return to Intake modal */}
      {showReturnModal && (
        <>
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => !modalSubmitting && setShowReturnModal(false)} />
          <div className="fixed inset-0 z-[61] flex items-center justify-center p-4">
            <div className="bg-slate-900 border border-slate-700/60 rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] overflow-y-auto">
              {/* Header */}
              <div className="px-6 pt-5 pb-4 border-b border-slate-800/60">
                <div className="flex items-center gap-3 mb-1">
                  <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                    <Undo2 className="w-4 h-4 text-amber-400" />
                  </div>
                  <h3 className="text-base font-semibold text-white">Return to Intake</h3>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Send this referral back to intake for additional information.
                </p>
              </div>

              {/* Reason code */}
              <div className="px-6 pt-4 pb-3">
                <p className="text-xs font-medium text-slate-400 mb-2">Reason *</p>
                <div className="space-y-1.5">
                  {Object.values(RETURN_REASON_CODES).map((code) => (
                    <label
                      key={code}
                      className={`flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer transition-all text-sm ${
                        returnReason === code
                          ? 'bg-amber-500/15 border border-amber-500/30 text-amber-200'
                          : 'bg-slate-800/40 border border-slate-700/30 text-slate-400 hover:bg-slate-800/60'
                      }`}
                    >
                      <input
                        type="radio"
                        name="returnReason"
                        value={code}
                        checked={returnReason === code}
                        onChange={() => setReturnReason(code)}
                        className="sr-only"
                      />
                      <span className={`w-3.5 h-3.5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                        returnReason === code ? 'border-amber-400' : 'border-slate-600'
                      }`}>
                        {returnReason === code && <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />}
                      </span>
                      {RETURN_REASON_LABELS[code] ?? code}
                    </label>
                  ))}
                </div>
              </div>

              {/* Missing item types */}
              <div className="px-6 pt-2 pb-3">
                <p className="text-xs font-medium text-slate-400 mb-2">Missing Items *</p>
                <div className="space-y-1.5">
                  {Object.values(REWORK_ITEM_TYPE).map((type) => (
                    <label
                      key={type}
                      className={`flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer transition-all text-sm ${
                        returnItems.includes(type)
                          ? 'bg-violet-500/15 border border-violet-500/30 text-violet-200'
                          : 'bg-slate-800/40 border border-slate-700/30 text-slate-400 hover:bg-slate-800/60'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={returnItems.includes(type)}
                        onChange={() => toggleReturnItem(type)}
                        className="sr-only"
                      />
                      <span className={`w-4 h-4 rounded border-2 flex-shrink-0 flex items-center justify-center text-[10px] ${
                        returnItems.includes(type) ? 'border-violet-400 bg-violet-500/30 text-violet-300' : 'border-slate-600'
                      }`}>
                        {returnItems.includes(type) && '✓'}
                      </span>
                      {REWORK_ITEM_TYPE_LABELS[type] ?? type}
                    </label>
                  ))}
                </div>
              </div>

              {/* Notify — Intake Coordinator receiver (required) */}
              <div className="px-6 pt-2 pb-2">
                <p className="text-xs font-medium text-slate-400 mb-2">Notify Intake Coordinator</p>
                {intakeError ? (
                  <p className="text-xs text-red-400">{intakeError}</p>
                ) : (!intakeLoading && intakeUsers.length === 0) ? (
                  <p className="text-xs text-amber-400">No active Intake Coordinator is available to receive this handoff.</p>
                ) : (
                  <select
                    value={returnReceiver}
                    onChange={(e) => { setReturnReceiver(e.target.value); setReturnError(null); }}
                    disabled={modalSubmitting || intakeLoading}
                    className="w-full rounded-lg bg-slate-800/60 border border-slate-700/40 text-sm text-white px-3 py-2 focus:outline-none focus:ring-1 focus:ring-amber-500/40"
                  >
                    <option value="">{intakeLoading ? 'Loading…' : 'Select Intake Coordinator…'}</option>
                    {intakeUsers.map((u) => {
                      const ru = u.uid || u.id;
                      return (
                        <option key={ru} value={ru}>
                          {u.displayName || u.name || u.email || ru} (Intake Coordinator)
                        </option>
                      );
                    })}
                  </select>
                )}
              </div>

              {/* Optional note */}
              <div className="px-6 pt-2 pb-4">
                <p className="text-xs font-medium text-slate-400 mb-2">Note (optional · non-PHI)</p>
                <textarea
                  value={returnNote}
                  onChange={(e) => setReturnNote(e.target.value)}
                  placeholder="Additional non-PHI context for intake…"
                  rows={3}
                  className="w-full rounded-lg bg-slate-800/60 border border-slate-700/40 text-sm text-slate-200 placeholder-slate-600 px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-amber-500/40"
                />
                {returnError && <p className="text-xs text-red-400 mt-2">{returnError}</p>}
              </div>

              {/* Actions */}
              <div className="px-6 pb-5 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => { setShowReturnModal(false); setReturnReason(''); setReturnItems([]); setReturnNote(''); setReturnReceiver(''); setReturnError(null); }}
                  disabled={modalSubmitting}
                  className="flex-1 py-2.5 rounded-xl border border-slate-700/50 text-slate-400 text-sm font-medium hover:bg-slate-800/50 transition-colors disabled:opacity-40"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={doReturn}
                  disabled={modalSubmitting || !canSubmitReturn}
                  className="flex-1 py-2.5 rounded-xl bg-amber-600/30 hover:bg-amber-500/40 text-amber-300 text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {modalSubmitting
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Returning…</>
                    : <><Undo2 className="w-4 h-4" /> Return to Intake</>}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Footer buttons — portalled into the drawer footer container */}
      {footerEl && createPortal(
        <div className="px-6 py-4 border-t border-slate-800/50 flex-shrink-0 space-y-3">
          {/* Book Appointment / Set Scheduled */}
          {canSchedule && (
            <button
              onClick={() => setShowConfirmScheduled(true)}
              disabled={!!actionLoading}
              data-learning-target="book-appointment-action"
              className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium disabled:opacity-40 transition-colors${activeFocusTarget === 'book-appointment-action' ? ' learning-focus-highlight-subtle' : ''}`}
            >
              {actionLoading === 'schedule'
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <Calendar className="w-4 h-4" />}
              Book Appointment
            </button>
          )}
          {/* Set Seen */}
          {isScheduled && canSee && (
            <button
              onClick={() => setShowConfirmSeen(true)}
              disabled={!!actionLoading}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium disabled:opacity-40 transition-colors"
            >
              {actionLoading === 'seen'
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <Eye className="w-4 h-4" />}
              Mark Seen
            </button>
          )}
          {/* Return to Intake */}
          {canSchedule && (
            <button
              onClick={() => setShowReturnModal(true)}
              disabled={!!actionLoading}
              data-learning-target="scheduling-return-to-intake"
              className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 text-sm font-medium disabled:opacity-40 transition-colors${activeFocusTarget === 'scheduling-return-to-intake' ? ' learning-focus-highlight-subtle' : ''}`}
            >
              <Undo2 className="w-4 h-4" />
              Return to Intake
            </button>
          )}
        </div>,
        footerEl
      )}
    </>
  );
}

function TenantAdminPanel({
  workflow, tenantId, referralId, uid,
  onAction, actionLoading, actionError, onAfterAction,
  onShowCreateModal, onShowTouchModal,
}) {
  const isOwner    = workflow?.ownerUid === uid;
  const ALL_STATUSES = Object.values(WORKFLOW_STATUS);
  const PRIORITIES   = Object.values(WORKFLOW_PRIORITY);

  return (
    <div className="space-y-4">
      {/* Ownership */}
      <div>
        <p className="text-xs font-medium text-slate-400 mb-2">Ownership</p>
        <div className="flex gap-2">
          {!isOwner ? (
            <button
              onClick={() => onAction('claim', () => claimOwner(tenantId, referralId, workflow, uid, 'tenant_admin'))}
              disabled={!!actionLoading}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-violet-600/20 hover:bg-violet-500/30 text-violet-400 text-xs font-medium disabled:opacity-50 transition-all"
            >
              {actionLoading === 'claim' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserCheck className="w-3.5 h-3.5" />}
              Claim
            </button>
          ) : (
            <button
              onClick={() => onAction('release', () => releaseOwner(tenantId, referralId, workflow, uid, 'tenant_admin'))}
              disabled={!!actionLoading}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-400 text-xs font-medium disabled:opacity-50 transition-all"
            >
              {actionLoading === 'release' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserMinus className="w-3.5 h-3.5" />}
              Release
            </button>
          )}
        </div>
      </div>

      {/* Status */}
      <div>
        <p className="text-xs font-medium text-slate-400 mb-2">Status</p>
        <div className="flex flex-wrap gap-2">
          {ALL_STATUSES.map((s) => {
            const cfg = STATUS_CONFIG[s];
            const isCurrent = workflow?.status === s;
            return (
              <button
                key={s}
                onClick={() => onAction(`status_${s}`, () => updateWorkflowStatus(tenantId, referralId, workflow, s, uid, 'tenant_admin'))}
                disabled={!!actionLoading || isCurrent}
                className={`px-2.5 py-1 rounded-lg text-xs font-medium disabled:opacity-50 transition-all border
                  ${isCurrent ? cfg.tw + ' cursor-default' : 'bg-slate-800 border-slate-700/50 text-slate-400 hover:bg-slate-700 hover:text-white'}`}
              >
                {actionLoading === `status_${s}` && <Loader2 className="w-3 h-3 animate-spin inline mr-1" />}
                {cfg?.label ?? s}
              </button>
            );
          })}
        </div>
      </div>

      {/* Priority */}
      <div>
        <p className="text-xs font-medium text-slate-400 mb-2">Priority</p>
        <div className="flex gap-2">
          {PRIORITIES.map((p) => {
            const cfg = PRIORITY_CONFIG[p];
            const isCurrent = workflow?.priority === p;
            return (
              <button
                key={p}
                onClick={() => onAction(`priority_${p}`, () => updateWorkflowPriority(tenantId, referralId, workflow, p, uid, 'tenant_admin'))}
                disabled={!!actionLoading || isCurrent}
                className={`px-2.5 py-1 rounded-lg text-xs font-medium disabled:opacity-50 transition-all border
                  ${isCurrent ? cfg.tw + ' cursor-default' : 'bg-slate-800 border-slate-700/50 text-slate-400 hover:bg-slate-700 hover:text-white'}`}
              >
                {actionLoading === `priority_${p}` && <Loader2 className="w-3 h-3 animate-spin inline mr-1" />}
                {cfg?.label ?? p}
              </button>
            );
          })}
        </div>
      </div>

      {/* Rework actions */}
      <div>
        <p className="text-xs font-medium text-slate-400 mb-2">Rework</p>
        <div className="flex gap-2">
          <button
            onClick={onShowCreateModal}
            disabled={!!actionLoading}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-amber-600/20 hover:bg-amber-500/30 text-amber-400 text-xs font-medium disabled:opacity-50 transition-all"
          >
            <PackageSearch className="w-3.5 h-3.5" />
            Request Missing Items
          </button>
          <button
            onClick={onShowTouchModal}
            disabled={!!actionLoading}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-teal-600/20 hover:bg-teal-500/30 text-teal-400 text-xs font-medium disabled:opacity-50 transition-all"
          >
            <PhoneCall className="w-3.5 h-3.5" />
            Log Touch
          </button>
        </div>
      </div>

      <ActionError msg={actionError} onRetry={onAfterAction} />
    </div>
  );
}

// ── Event history ────────────────────────────────────────────────────────────

function EventHistory({ events, loading, workflow, usersMap }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="w-5 h-5 text-slate-500 animate-spin" />
      </div>
    );
  }
  if (events.length === 0) {
    return <p className="text-xs text-slate-500 py-4 text-center">No events yet.</p>;
  }
  return (
    <div className="space-y-2">
      {events.map((ev) => {
        const label = EVENT_LABELS[ev.eventType] ?? ev.eventType;
        const actor = ev.actorUid ? usersMap?.[ev.actorUid] : null;
        const actorName = actor?.name ?? null;
        const actorRole = ev.actorRole ? (ROLE_DISPLAY[ev.actorRole]?.name ?? ev.actorRole) : null;
        return (
          <div key={ev.id} className="flex items-start gap-3 py-2 border-b border-slate-800/60 last:border-0">
            <div className="w-5 h-5 rounded-full bg-slate-800 border border-slate-700/50 flex items-center justify-center flex-shrink-0 mt-0.5">
              <div className="w-1.5 h-1.5 rounded-full bg-slate-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-slate-300">{label}</p>
              {(ev.fromStatus || ev.toStatus) && ev.fromStatus !== ev.toStatus && (
                <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                  <span>{STATUS_CONFIG[ev.fromStatus]?.label ?? ev.fromStatus ?? '—'}</span>
                  <ArrowRight className="w-3 h-3" />
                  <span>{STATUS_CONFIG[ev.toStatus]?.label ?? ev.toStatus ?? '—'}</span>
                </p>
              )}
              {ev.itemType && (
                <p className="text-xs text-slate-500 mt-0.5">
                  {REWORK_ITEM_TYPE_LABELS[ev.itemType] ?? ev.itemType}
                  {ev.fromItemStatus && ev.toItemStatus && ev.fromItemStatus !== ev.toItemStatus && (
                    <span className="ml-1">
                      ({REWORK_ITEM_STATUS_CONFIG[ev.fromItemStatus]?.label ?? ev.fromItemStatus}
                      {' → '}
                      {REWORK_ITEM_STATUS_CONFIG[ev.toItemStatus]?.label ?? ev.toItemStatus})
                    </span>
                  )}
                </p>
              )}
              {ev.eventType === 'CLOSED_LOOP_SET' && workflow?.closedLoop?.closedLoopNote && (
                <p className="text-xs text-slate-400 mt-1 italic">
                  &ldquo;{workflow.closedLoop.closedLoopNote}&rdquo;
                </p>
              )}
              <p className="text-xs text-slate-600 mt-0.5">
                {fmtTs(ev.createdAt)}
                {(actorRole || actorName) && (
                  <span className="ml-1.5">
                    {'· '}
                    {actorRole}{actorRole && actorName && ', '}{actorName}
                  </span>
                )}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function WorkItemDrawer({ item, onClose, onWorkflowUpdated, activeFocusTarget }) {
  const { user } = useAuth();
  const role      = user?.role ?? '';
  const uid       = user?.uid  ?? '';
  const tenantId  = item.referral?.tenantId ?? user?.tenantId ?? '';
  const referralId = item.referral?.id;

  // Stage 2 state
  const [workflow, setWorkflow]           = useState(item.workflow);
  const [events, setEvents]               = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [usersMap, setUsersMap]           = useState({});
  const [actionLoading, setActionLoading] = useState('');
  const [actionError, setActionError]     = useState('');
  const [toast, setToast]                 = useState(null);

  // Stage 3 state
  const [reworkRequests, setReworkRequests] = useState([]);
  const [reworkTouches, setReworkTouches]   = useState([]);
  const [reworkLoading, setReworkLoading]   = useState(false);
  const [reworkError, setReworkError]       = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTouchModal, setShowTouchModal]   = useState(false);
  const [modalSubmitting, setModalSubmitting] = useState(false);

  // Whether the current role can write rework actions
  const canWriteRework = ['intake_coordinator', 'triage_nurse', 'tenant_admin', 'platform_admin'].includes(role);

  // PHI panel state (Step 2)
  const [phiOpen, setPhiOpen]               = useState(false);
  const [phiState, setPhiState]             = useState('idle'); // 'idle'|'loading'|'loaded'|'error'
  const [phiError, setPhiError]             = useState(null);
  const [identityDraft, setIdentityDraft]   = useState({});
  const [contactDraft, setContactDraft]     = useState({});
  const [insuranceDraft, setInsuranceDraft] = useState({});

  // Attachments panel state (PHI Step 3)
  const [attOpen, setAttOpen]         = useState(false);
  const [attState, setAttState]       = useState('idle'); // 'idle'|'loading'|'loaded'|'error'|'access-denied'
  const [attachments, setAttachments] = useState([]);
  const [attError, setAttError]       = useState(null);

  const [uploadState, setUploadState] = useState('idle'); // 'idle'|'uploading'|'finalizing'|'done'|'error'
  const [uploadErr, setUploadErr]     = useState(null);
  const [uploadType, setUploadType]   = useState('PDF'); // LABS|IMAGING|PDF|PHOTO|OTHER
  const fileInputRef                  = useRef(null);
  const [schedulerFooterEl, setSchedulerFooterEl] = useState(null);

  useEffect(() => {
    if (!activeFocusTarget) return;
    const el = document.querySelector(`[data-learning-target="${activeFocusTarget}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [activeFocusTarget]);

  // Refs that let the rework subscription callback read the latest closure values
  // without stale captures. Updated whenever the referenced values change.
  const attOpenRef         = useRef(attOpen);
  const loadAttachmentsRef = useRef(null);
  useEffect(() => { attOpenRef.current = attOpen; }, [attOpen]);

  // Load events + rework data on open
  useEffect(() => {
    if (!referralId) return;
    setEventsLoading(true);
    setReworkLoading(true);

    Promise.all([
      listWorkflowEvents(tenantId, referralId),
      listReworkRequests(tenantId, referralId),
      listReworkTouches(tenantId, referralId),
    ])
      .then(([newEvents, reqs, touches]) => {
        setEvents(newEvents);
        setReworkRequests(reqs);
        setReworkTouches(touches);
      })
      .catch(() => {
        setReworkError('Could not load rework data.');
      })
      .finally(() => {
        setEventsLoading(false);
        setReworkLoading(false);
      });
  }, [tenantId, referralId]);

  useEffect(() => {
    if (!tenantId || events.length === 0) return;
    const uids = [...new Set(events.map((e) => e.actorUid).filter(Boolean))];
    const missing = uids.filter((id) => !usersMap[id]);
    if (missing.length === 0) return;
    let cancelled = false;
    Promise.all(missing.map((id) => getTenantUser(tenantId, id).then((u) => [id, u]).catch(() => [id, null])))
      .then((pairs) => {
        if (cancelled) return;
        setUsersMap((prev) => {
          const next = { ...prev };
          for (const [id, u] of pairs) { if (u) next[id] = u; }
          return next;
        });
      });
    return () => { cancelled = true; };
  }, [tenantId, events]);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  // Refresh workflow + events + rework after any write
  const afterAction = useCallback(async () => {
    const [newWorkflow, newEvents, reqs, touches] = await Promise.all([
      getWorkflowState(tenantId, referralId),
      listWorkflowEvents(tenantId, referralId),
      listReworkRequests(tenantId, referralId),
      listReworkTouches(tenantId, referralId),
    ]);
    setWorkflow(newWorkflow);
    setEvents(newEvents);
    setReworkRequests(reqs);
    setReworkTouches(touches);
    onWorkflowUpdated?.(referralId, newWorkflow);
  }, [tenantId, referralId, onWorkflowUpdated]);

  // Generic action runner (Stage 2 actions)
  const doAction = useCallback(async (key, fn) => {
    setActionLoading(key);
    setActionError('');
    try {
      await fn();
      showToast('Updated successfully.');
      await afterAction();
    } catch (err) {
      const msg = err?.message ?? 'Action failed. Please try again.';
      setActionError(msg);
      showToast(msg, 'error');
      // Best-effort state refresh so the UI reflects actual Firestore state before retry
      await afterAction().catch(() => {});
    } finally {
      setActionLoading('');
    }
  }, [afterAction, showToast]);

  // Stage 3: handle Create Request modal submit
  const handleCreateRequest = useCallback(async (selectedTypes) => {
    setModalSubmitting(true);
    try {
      await createReworkRequest(tenantId, referralId, workflow, selectedTypes, uid, role);
      setShowCreateModal(false);
      showToast('Missing items request created.');
      await afterAction();
    } catch (err) {
      showToast(err?.message ?? 'Failed to create request.', 'error');
    } finally {
      setModalSubmitting(false);
    }
  }, [tenantId, referralId, workflow, uid, role, afterAction, showToast]);

  // Stage 3: handle Log Touch modal submit
  const handleLogTouch = useCallback(async (channel, direction, outcome) => {
    setModalSubmitting(true);
    try {
      await logReworkTouch(tenantId, referralId, workflow, channel, direction, outcome, uid, role);
      setShowTouchModal(false);
      showToast('Touch logged.');
      await afterAction();
    } catch (err) {
      showToast(err?.message ?? 'Failed to log touch.', 'error');
    } finally {
      setModalSubmitting(false);
    }
  }, [tenantId, referralId, workflow, uid, role, afterAction, showToast]);


  // PHI Step 2.1: load PHI docs; skip insurance for roles that can't read it (avoids 403)
  const canReadInsurance = PHI_INSURANCE_READ_ROLES.includes(role);

  const loadPhiData = useCallback(async () => {
    if (phiState === 'loading') return;
    setPhiState('loading');
    setPhiError(null);
    try {
      const promises = [
        getPatientIdentity(referralId),
        getContact(referralId),
      ];
      if (canReadInsurance) promises.push(getInsurance(referralId));

      const results = await Promise.allSettled(promises);
      const [idResult, conResult] = results;
      const insResult = canReadInsurance ? results[2] : null;

      // Check if critical docs both failed with permission-denied → full access-denied state
      const criticalResults = [idResult, conResult];
      const allDenied = criticalResults.every(
        r => r.status === 'rejected' && r.reason?.code === 'functions/permission-denied'
      );
      if (allDenied) {
        setPhiState('access-denied');
        return;
      }

      // Check if any non-permission-denied error occurred on identity or contact (critical docs)
      const criticalError = criticalResults.find(
        r => r.status === 'rejected' && r.reason?.code !== 'functions/permission-denied'
      );
      if (criticalError) {
        const err = criticalError.reason;
        setPhiError(safeUserError(err) ?? 'Unable to load PHI');
        setPhiState('error');
        return;
      }

      // Extract data — permission-denied on individual docs yields empty object (graceful)
      const identity  = idResult.status === 'fulfilled' && idResult.value.exists  ? idResult.value.data  : {};
      const contact   = conResult.status === 'fulfilled' && conResult.value.exists ? conResult.value.data : {};
      const insurance = insResult && insResult.status === 'fulfilled' && insResult.value.exists ? insResult.value.data : {};
      setIdentityDraft(identity);
      setContactDraft(contact);
      setInsuranceDraft(insurance);
      setPhiState('loaded');
    } catch (err) {
      setPhiError(safeUserError(err) ?? 'Unable to load PHI');
      setPhiState('error');
    }
  }, [referralId, phiState, canReadInsurance]);

  const handlePhiToggle = useCallback(() => {
    if (!phiOpen && phiState === 'idle') loadPhiData(); // lazy-load on first expand
    setPhiOpen(v => !v);
  }, [phiOpen, phiState, loadPhiData]);


  // ── Attachment callbacks (PHI Step 3) ─────────────────────────────────────

  const loadAttachments = useCallback(async () => {
    if (attState === 'loading') return;
    setAttState('loading');
    setAttError(null);
    try {
      const res = await listAttachments(referralId);
      setAttachments(res.attachments ?? []);
      setAttState('loaded');
    } catch (err) {
      if (err?.code === 'functions/permission-denied') {
        setAttState('access-denied');
      } else {
        setAttError(safeUserError(err) ?? 'Unable to load attachments');
        setAttState('error');
      }
    }
  }, [referralId, attState]);

  // Keep the ref in sync so the snapshot callback always calls the latest version.
  useEffect(() => { loadAttachmentsRef.current = loadAttachments; }, [loadAttachments]);

  // ── Real-time rework subscription ─────────────────────────────────────────
  // Subscribes to reworkRequests for this referral so the intake coordinator
  // sees GP uploads resolve without needing to navigate away.
  //
  // When all open requests close:
  //   • refresh workflow state + notify parent (list row updates to READY_TO_SCHEDULE)
  //   • reload attachment manifests if the panel was already open
  useEffect(() => {
    if (!referralId || !tenantId) return;
    const db     = getFirestore(getApp());
    const colRef = collection(db, 'tenants', tenantId, 'referrals', referralId, 'reworkRequests');
    let prevOpenCount = -1; // -1 = initial snapshot, not a real transition

    const unsub = onSnapshot(
      colRef,
      (snap) => {
        const docs      = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const openCount = docs.filter((d) => d.status === 'open').length;

        // Always update the rework list so rows reflect current Firestore truth.
        setReworkRequests(docs);

        // When the last open request closes (GP uploaded final item), refresh
        // workflow and attachments to reflect the READY_TO_SCHEDULE promotion.
        if (prevOpenCount > 0 && openCount === 0) {
          getWorkflowState(tenantId, referralId)
            .then((wf) => {
              setWorkflow(wf);
              onWorkflowUpdated?.(referralId, wf);
            })
            .catch(() => {});

          // Reload attachment manifests if the panel has already been opened.
          if (attOpenRef.current && PHI_DOWNLOAD_ROLES.includes(role)) {
            loadAttachmentsRef.current?.();
          }
        }

        prevOpenCount = openCount;
      },
      () => { /* permission errors are silently ignored */ },
    );

    return unsub;
  }, [referralId, tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpload = useCallback(async (file) => {
    if (!file) return;
    setUploadState('uploading');
    setUploadErr(null);
    try {
      // 1. Create manifest + get signed upload URL
      const { attachmentId, uploadUrl } = await createAttachmentUploadUrl(referralId, {
        mimeType:     file.type || 'application/octet-stream',
        originalName: file.name,
        type:         uploadType,
      });
      // 2. PUT file directly to GCS via signed URL
      const putRes = await fetch(uploadUrl, {
        method:  'PUT',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body:    file,
      });
      if (!putRes.ok) throw new Error(`Storage upload failed (${putRes.status})`);
      // 3. Finalize — mark manifest ACTIVE
      setUploadState('finalizing');
      await finalizeAttachmentUpload(referralId, attachmentId);
      setUploadState('done');
      await loadAttachments();
    } catch (err) {
      setUploadErr(err.message ?? 'Upload failed');
      setUploadState('error');
    }
  }, [referralId, uploadType, loadAttachments]);



  // Lazy-load attachments on first open (mirrors PHI panel pattern)
  useEffect(() => {
    if (attOpen && attState === 'idle' && PHI_DOWNLOAD_ROLES.includes(role)) {
      loadAttachments();
    }
  }, [attOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Determine which action panel to render
  const renderActionPanel = () => {
    if (!workflow) {
      return <p className="text-xs text-slate-500">Workflow state unavailable.</p>;
    }
    const sharedProps = {
      workflow, tenantId, referralId, referral: item.referral, uid, role,
      reworkRequests,
      onAction: doAction, actionLoading, actionError,
      onAfterAction: afterAction,
      onShowCreateModal: () => setShowCreateModal(true),
      onShowTouchModal:  () => setShowTouchModal(true),
      activeFocusTarget,
    };

    switch (role) {
      case 'intake_coordinator': return <IntakePanel {...sharedProps} />;
      case 'triage_nurse':       return <TriagePanel {...sharedProps} />;
      case 'scheduler':          return <SchedulerPanel {...sharedProps} footerEl={schedulerFooterEl} activeFocusTarget={activeFocusTarget} />;
      case 'tenant_admin':
      case 'platform_admin':     return <TenantAdminPanel {...sharedProps} />;
      case 'ops_manager':        return <p className="text-xs text-slate-500 italic">Read-only access — no actions available.</p>;
      default:                   return null;
    }
  };

  const referral    = item.referral ?? {};
  const outcomeSt   = referral.outcome?.status ?? referral.outcomeStatus ?? null;
  const outcomeCfg  = OUTCOME_CONFIG[outcomeSt] ?? null;
  const statusCfg   = STATUS_CONFIG[workflow?.status] ?? null;
  const priorityCfg = PRIORITY_CONFIG[workflow?.priority] ?? PRIORITY_CONFIG[WORKFLOW_PRIORITY.NORMAL];
  const triageKey       = workflow?.triage?.triageDecision;
  const triageCompleted = !!(workflow?.triage?.triageCompletedAt || workflow?.triageCompletedAt);
  const triageCfg       = (triageKey === TRIAGE_DECISION.NONE || !triageKey) && triageCompleted
    ? TRIAGE_CONFIG._COMPLETED
    : TRIAGE_CONFIG[triageKey] ?? TRIAGE_CONFIG[TRIAGE_DECISION.NONE];
  const isOwned     = !!workflow?.ownerUid;
  const isOwnedByMe = workflow?.ownerUid === uid;
  const isStuck     = computeStuck(workflow);

  const actionPanel = renderActionPanel();

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />

      {/* Panel */}
      <div className={`fixed inset-y-0 right-0 w-full max-w-md z-50 flex flex-col bg-slate-900 border-l border-slate-700/50 shadow-2xl overflow-hidden${activeFocusTarget === 'referral-review-panel' ? ' learning-focus-highlight-subtle' : ''}`} data-learning-target="referral-review-panel">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700/50 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-slate-800 border border-slate-700/50 flex items-center justify-center flex-shrink-0">
              <ClipboardList className="w-4 h-4 text-slate-400" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white truncate">
                {role === 'intake_coordinator' ? 'Intake Review Panel' : 'Referral Receipt'}: {referral.specialtyId ?? 'Unknown Specialty'} {referral.packId ?? ''}
              </p>
              <p className="text-xs text-slate-500 truncate font-mono">{referralId}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors flex-shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-6 py-5 space-y-6">

            {/* Referral summary */}
            <div className={activeFocusTarget === 'referral-summary-section' ? 'rounded-xl transition-all duration-300 learning-focus-highlight-subtle' : ''} data-learning-target="referral-summary-section">
              <SectionTitle>Referral Summary</SectionTitle>
              <div className="bg-slate-800/40 rounded-xl p-4 space-y-0.5 border border-slate-700/30">
                <InfoRow label="Outcome">
                  {outcomeCfg
                    ? <Badge tw={outcomeCfg.tw} label={outcomeCfg.label} />
                    : (() => {
                        if (outcomeSt) {
                          console.warn('[WorkItemDrawer] Unknown outcome.status value — update OUTCOME_CONFIG:', outcomeSt);
                        } else {
                          console.warn('[WorkItemDrawer] outcome.status missing on referral:', referral.referralId ?? item?.id);
                        }
                        return <span className="text-amber-400/70 text-xs italic">Outcome unavailable</span>;
                      })()}
                </InfoRow>
                <InfoRow label="Specialty">{referral.specialtyId ?? '—'}</InfoRow>
                <InfoRow label="Pack">{referral.packId ?? '—'}</InfoRow>
                <InfoRow label="Submitted">{fmtTs(referral.createdAt ?? referral.savedAt)}</InfoRow>
                {referral.createdByUid && (
                  <InfoRow label="Submitted by"><MemberName uid={referral.createdByUid} /></InfoRow>
                )}
              </div>
            </div>

            {/* Override Context — shown only when an override exists */}
            {isOverridden(referral, workflow) && (() => {
              const od = getOverrideDetail(referral, workflow);
              return (
                <div>
                  <SectionTitle>Override Context</SectionTitle>
                  <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4 space-y-0.5">
                    <InfoRow label="Exception">
                      <Badge tw="bg-amber-500/15 text-amber-300 border-amber-500/20" label="Overridden" />
                    </InfoRow>
                    <InfoRow label="Gate Outcome">
                      {outcomeCfg
                        ? <Badge tw={outcomeCfg.tw} label={outcomeCfg.label} />
                        : <span className="text-slate-500 text-xs">—</span>}
                    </InfoRow>
                    <InfoRow label="Override Reason">
                      <span className="text-slate-200 break-words text-right">{od.reason || '—'}</span>
                    </InfoRow>
                    <InfoRow label="Override Note">
                      <span className="text-slate-300 break-words text-right block max-w-[200px]">{od.note || '—'}</span>
                    </InfoRow>
                    <InfoRow label="Overridden By">
                      <MemberName uid={od.by} showRole />
                    </InfoRow>
                    <InfoRow label="Overridden At">
                      {fmtTs(od.at)}
                    </InfoRow>
                    <InfoRow label="Current Status">
                      {statusCfg
                        ? <Badge tw={statusCfg.tw} label={statusCfg.label} />
                        : <span className="text-slate-500 text-xs">—</span>}
                    </InfoRow>
                    <InfoRow label="Current Priority">
                      <Badge tw={priorityCfg.tw} label={priorityCfg.label} />
                    </InfoRow>
                  </div>
                </div>
              );
            })()}

            {/* Workflow state */}
            <div className={activeFocusTarget === 'workflow-state-section' ? 'rounded-xl transition-all duration-300 learning-focus-highlight-subtle' : ''} data-learning-target="workflow-state-section">
              <SectionTitle>Workflow State</SectionTitle>
              <div className="bg-slate-800/40 rounded-xl p-4 space-y-0.5 border border-slate-700/30">
                <InfoRow label="Status">
                  <div className="flex items-center gap-1.5 flex-wrap justify-end">
                    {statusCfg
                      ? <Badge tw={statusCfg.tw} label={statusCfg.label} />
                      : <span className="text-slate-500">—</span>}
                    {isStuck && (
                      <Badge tw="bg-red-500/15 text-red-300 border-red-500/20" label="Stuck" />
                    )}
                  </div>
                </InfoRow>
                <InfoRow label="Priority">
                  <Badge tw={priorityCfg.tw} label={priorityCfg.label} />
                </InfoRow>
                <InfoRow label="Owner">
                  {isOwned ? (
                    isOwnedByMe
                      ? <span className="text-xs font-medium text-teal-300">You</span>
                      : <MemberName uid={workflow.ownerUid} />
                  ) : (
                    <span className="text-slate-500">Unassigned</span>
                  )}
                </InfoRow>
                {role !== 'intake_coordinator' && (
                  <InfoRow label="Triage">
                    <Badge tw={triageCfg.tw} label={triageCfg.label} />
                  </InfoRow>
                )}
                {role !== 'intake_coordinator' && workflow?.triage?.triageNote && (
                  <InfoRow label="Triage Note">
                    <span className="text-slate-300 text-xs break-words text-right block max-w-[200px]">
                      {workflow.triage.triageNote}
                    </span>
                  </InfoRow>
                )}
                {workflow?.rework?.missingItemsCount > 0 && (
                  <InfoRow label="Missing Items">
                    <span className="text-amber-300 font-medium">{workflow.rework.missingItemsCount}</span>
                  </InfoRow>
                )}
                {workflow?.rework?.attempts > 0 && (
                  <InfoRow label="Touch Attempts">
                    <span className="text-slate-300">{workflow.rework.attempts}</span>
                  </InfoRow>
                )}
                {workflow?.scheduling?.scheduledAt && (
                  <InfoRow label="Scheduled">{fmtDate(workflow.scheduling.scheduledAt)}</InfoRow>
                )}
                {workflow?.scheduling?.seenAt && (
                  <InfoRow label="Seen at">{fmtDate(workflow.scheduling.seenAt)}</InfoRow>
                )}
                {workflow?.closedLoop?.closedAt && (
                  <InfoRow label="Closed">{fmtDate(workflow.closedLoop.closedAt)}</InfoRow>
                )}
                <InfoRow label="Updated">{fmtTs(workflow?.updatedAt)}</InfoRow>
              </div>
            </div>

            {/* Action panel — positioned early so intake sees controls after context */}
            {actionPanel && (
              <div className={activeFocusTarget === 'intake-actions-section' ? 'rounded-xl transition-all duration-300 learning-focus-highlight-subtle' : ''} data-learning-target="intake-actions-section">
                <SectionTitle>{role === 'intake_coordinator' ? 'Intake Actions' : 'Actions'}</SectionTitle>
                <div className="bg-slate-800/40 rounded-xl p-4 border border-slate-700/30">
                  {actionPanel}
                </div>
              </div>
            )}

            {/* Referral Ping composer — shared across creator roles (intake / triage /
                scheduler / tenant_admin). One referral, one action, one receiver.
                Learning focus target is role-scoped: this drawer serves intake on
                /workqueue and scheduler on /scheduling (one role at a time), and
                each role's learning module references its own target. */}
            {workflow && canCreateReferralPing(role) && (() => {
              const pingFocusTarget = role === 'scheduler' ? 'scheduler_referral_ping' : 'intake_review_referral_ping';
              return (
              <div
                className={activeFocusTarget === pingFocusTarget ? 'rounded-xl transition-all duration-300 learning-focus-highlight-subtle' : ''}
                data-learning-target={pingFocusTarget}
              >
                <SectionTitle>Referral Ping</SectionTitle>
                <div className="bg-slate-800/40 rounded-xl p-4 border border-slate-700/30">
                  <PingComposer
                    tenantId={tenantId}
                    referralId={referralId}
                    workflow={workflow}
                    uid={uid}
                    role={role}
                    reworkRequests={reworkRequests}
                    defaultPurpose={role === 'scheduler' ? COMMUNICATION_PURPOSE.BOOKING_BLOCKER : undefined}
                  />
                </div>
              </div>
              );
            })()}

            {/* Missing Items (Stage 3) */}
            <div className={activeFocusTarget === 'missing-items-section' ? 'rounded-xl transition-all duration-300 learning-focus-highlight-subtle' : ''} data-learning-target="missing-items-section">
              <div className="flex items-center gap-2 mb-3">
                <PackageSearch className="w-3.5 h-3.5 text-slate-500" />
                <SectionTitle>Missing Items</SectionTitle>
              </div>
              <div className="bg-slate-800/40 rounded-xl p-4 border border-slate-700/30">
                <MissingItemsSection
                  reworkRequests={reworkRequests}
                  reworkTouches={reworkTouches}
                  reworkLoading={reworkLoading}
                  reworkError={reworkError}
                />
              </div>
            </div>

            {/* Messaging — clipboard-first, zero PHI (Stage 3+) */}
            {canWriteRework && (
              <div className={activeFocusTarget === 'messaging-section' ? 'rounded-xl transition-all duration-300 learning-focus-highlight-subtle' : ''} data-learning-target="messaging-section">
                <div className="bg-slate-800/40 rounded-xl p-4 border border-slate-700/30">
                  <MessagingCard
                    tenantId={tenantId}
                    referralId={referralId}
                    specialtyName={referral.specialtyId ?? 'Specialty'}
                    packName={referral.packId ?? ''}
                    missingItemTypeLabels={
                      reworkRequests
                        .filter((r) => r.status === 'open')
                        .flatMap((r) => (r.items || []))
                        .filter((it) => it.itemStatus !== 'RECEIVED' && it.itemStatus !== 'PROVIDED')
                        .map((it) => REWORK_ITEM_TYPE_LABELS[it.itemType] ?? it.itemType)
                    }
                    notYetReasons={[]}
                    receiptToken={referral.receiptToken ?? null}
                    submittedAt={
                      referral.createdAt?.toDate
                        ? referral.createdAt.toDate().toISOString()
                        : referral.createdAt ?? null
                    }
                    actorUid={uid}
                    actorRole={role}
                    canLogTouch={canWriteRework}
                  />
                </div>
              </div>
            )}

            {/* Patient & Contact — PHI Step 2 */}
            {PHI_MODE && (
              <div className={activeFocusTarget === 'patient-contact-section' ? 'rounded-xl transition-all duration-300 learning-focus-highlight-subtle' : ''} data-learning-target="patient-contact-section">
                <button
                  onClick={handlePhiToggle}
                  className="w-full flex justify-between items-center mb-3 group"
                >
                  <div className="flex items-center gap-2">
                    <User className="w-3.5 h-3.5 text-slate-500" />
                    <SectionTitle>Patient &amp; Contact</SectionTitle>
                  </div>
                  <span className="text-slate-500 text-xs group-hover:text-slate-300 transition-colors">
                    {phiOpen ? '▲' : '▼'}
                  </span>
                </button>

                {phiOpen && (
                  <div className="bg-slate-800/40 rounded-xl p-4 border border-slate-700/30 space-y-4">
                    {/* Role gate */}
                    {!PHI_READ_ROLES.includes(role) && (
                      <p className="text-sm text-amber-300 bg-amber-900/20 rounded-lg p-3 border border-amber-700/30">
                        PHI access is not available for your role.
                      </p>
                    )}

                    {/* Loading */}
                    {PHI_READ_ROLES.includes(role) && phiState === 'loading' && (
                      <div className="flex items-center gap-2 text-sm text-slate-400">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Loading PHI…</span>
                      </div>
                    )}

                    {/* Error with retry */}
                    {PHI_READ_ROLES.includes(role) && phiState === 'error' && (
                      <div className="text-sm text-red-300 bg-red-900/20 rounded-lg p-3 border border-red-700/30 space-y-2">
                        <div className="flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 shrink-0" />
                          <span>Unable to load PHI — {phiError}</span>
                        </div>
                        <button
                          onClick={loadPhiData}
                          className="text-xs underline text-red-400 hover:text-red-200 transition-colors"
                        >
                          Retry
                        </button>
                      </div>
                    )}

                    {/* AccessDenied: PHI — permission-denied from server policy */}
                    {phiState === 'access-denied' && (
                      <div className="text-sm text-amber-300 bg-amber-900/20 rounded-lg p-3 border border-amber-700/30">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 shrink-0" />
                          <span>AccessDenied: PHI — your role is not permitted to view this data.</span>
                        </div>
                      </div>
                    )}

                    {/* Loaded form */}
                    {PHI_READ_ROLES.includes(role) && phiState === 'loaded' && (() => {
                      const idReadOnly  = !PHI_IDENTITY_EDIT_ROLES.includes(role);
                      const conReadOnly = !PHI_CONTACT_EDIT_ROLES.includes(role);
                      const roClass     = 'w-full bg-slate-800/30 border border-slate-700/30 rounded px-2 py-1.5 text-sm text-slate-400 cursor-default focus:outline-none';
                      const editClass   = 'w-full bg-slate-700/50 border border-slate-600/50 rounded px-2 py-1.5 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-slate-500';
                      return (
                      <div className="space-y-4">
                        {/* Patient Identity (read-only for intake_coordinator + scheduler) */}
                        <fieldset className="border border-slate-700/50 rounded-lg p-3 space-y-3">
                          <legend className="text-xs font-semibold text-slate-400 px-1">
                            Patient Identity{idReadOnly && <span className="ml-1.5 text-slate-600 font-normal">(read-only)</span>}
                          </legend>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs text-slate-500 mb-1">First Name</label>
                              <input
                                className={idReadOnly ? roClass : editClass}
                                readOnly={idReadOnly}
                                tabIndex={idReadOnly ? -1 : undefined}
                                value={identityDraft?.name?.first ?? ''}
                                onChange={idReadOnly ? undefined : e => setIdentityDraft(d => ({ ...d, name: { ...d.name, first: e.target.value } }))}
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-slate-500 mb-1">Last Name</label>
                              <input
                                className={idReadOnly ? roClass : editClass}
                                readOnly={idReadOnly}
                                tabIndex={idReadOnly ? -1 : undefined}
                                value={identityDraft?.name?.last ?? ''}
                                onChange={idReadOnly ? undefined : e => setIdentityDraft(d => ({ ...d, name: { ...d.name, last: e.target.value } }))}
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-slate-500 mb-1">Date of Birth</label>
                              <input
                                type="date"
                                className={idReadOnly ? roClass : editClass}
                                readOnly={idReadOnly}
                                tabIndex={idReadOnly ? -1 : undefined}
                                value={identityDraft?.dob ?? ''}
                                onChange={idReadOnly ? undefined : e => setIdentityDraft(d => ({ ...d, dob: e.target.value }))}
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-slate-500 mb-1">Sex</label>
                              <select
                                className={idReadOnly ? roClass : editClass}
                                disabled={idReadOnly}
                                tabIndex={idReadOnly ? -1 : undefined}
                                value={identityDraft?.sex ?? ''}
                                onChange={idReadOnly ? undefined : e => setIdentityDraft(d => ({ ...d, sex: e.target.value || null }))}
                              >
                                <option value="">—</option>
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                                <option value="other">Other</option>
                                <option value="unknown">Unknown / Prefer not to say</option>
                              </select>
                            </div>
                          </div>
                        </fieldset>

                        {/* Contact (read-only for intake_coordinator + scheduler) */}
                        <fieldset className="border border-slate-700/50 rounded-lg p-3 space-y-3">
                          <legend className="text-xs font-semibold text-slate-400 px-1">
                            Contact{conReadOnly && <span className="ml-1.5 text-slate-600 font-normal">(read-only)</span>}
                          </legend>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs text-slate-500 mb-1">Phone</label>
                              <input
                                className={conReadOnly ? roClass : editClass}
                                readOnly={conReadOnly}
                                tabIndex={conReadOnly ? -1 : undefined}
                                value={contactDraft?.phone ?? ''}
                                onChange={conReadOnly ? undefined : e => setContactDraft(d => ({ ...d, phone: e.target.value || null }))}
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-slate-500 mb-1">Email</label>
                              <input
                                type="email"
                                className={conReadOnly ? roClass : editClass}
                                readOnly={conReadOnly}
                                tabIndex={conReadOnly ? -1 : undefined}
                                value={contactDraft?.email ?? ''}
                                onChange={conReadOnly ? undefined : e => setContactDraft(d => ({ ...d, email: e.target.value || null }))}
                              />
                            </div>
                          </div>
                        </fieldset>

                        {/* Insurance — read-only, only shown to roles that can read it */}
                        {canReadInsurance && (
                          <fieldset className="border border-slate-700/50 rounded-lg p-3 space-y-3">
                            <legend className="text-xs font-semibold text-slate-400 px-1">Insurance <span className="ml-1.5 text-slate-600 font-normal">(read-only)</span></legend>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="block text-xs text-slate-500 mb-1">Payer Name</label>
                                <input
                                  className={roClass}
                                  value={insuranceDraft?.payerName ?? ''}
                                  readOnly
                                  tabIndex={-1}
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-slate-500 mb-1">Member ID</label>
                                <input
                                  className={roClass}
                                  value={insuranceDraft?.memberId ?? ''}
                                  readOnly
                                  tabIndex={-1}
                                />
                              </div>
                            </div>
                          </fieldset>
                        )}
                      </div>
                      ); })()}
                  </div>
                )}
              </div>
            )}

            {/* ── Attachments panel (PHI Step 3) ────────────────────────── */}
            {PHI_MODE && (
              <div className={activeFocusTarget === 'attachments-section' ? 'rounded-xl transition-all duration-300 learning-focus-highlight-subtle' : ''} data-learning-target="attachments-section">
                {/* Hidden file input — triggered by Upload button */}
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.tiff,.doc,.docx"
                  onChange={(e) => {
                    if (e.target.files?.[0]) handleUpload(e.target.files[0]);
                    e.target.value = '';
                  }}
                />

                {/* Accordion header */}
                <button
                  onClick={() => setAttOpen(o => !o)}
                  className="w-full flex justify-between items-center mb-3 group"
                >
                  <div className="flex items-center gap-2">
                    <Paperclip className="w-3.5 h-3.5 text-slate-500" />
                    <SectionTitle>
                      Attachments
                      {attState === 'loaded' && attachments.length > 0 && (
                        <span className="ml-1 font-normal text-slate-500 normal-case tracking-normal">
                          ({attachments.length})
                        </span>
                      )}
                    </SectionTitle>
                  </div>
                  <span className="text-slate-500 text-xs group-hover:text-slate-300 transition-colors">
                    {attOpen ? '▲' : '▼'}
                  </span>
                </button>

                {attOpen && (
                  <div className="space-y-3 mb-4">
                    {/* Role gate */}
                    {!PHI_DOWNLOAD_ROLES.includes(role) && (
                      <p className="text-xs text-slate-500 italic px-1">
                        Attachments are not available for your role.
                      </p>
                    )}

                    {/* Loading */}
                    {PHI_DOWNLOAD_ROLES.includes(role) && attState === 'loading' && (
                      <div className="flex items-center gap-2 text-slate-400 text-sm px-1">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Loading attachments…</span>
                      </div>
                    )}

                    {/* Error with retry */}
                    {PHI_DOWNLOAD_ROLES.includes(role) && attState === 'error' && (
                      <div className="text-sm text-red-300 bg-red-900/20 rounded-lg p-3 border border-red-700/30">
                        <div className="flex items-center justify-between gap-2">
                          <span>{attError}</span>
                          <button
                            onClick={() => { setAttState('idle'); loadAttachments(); }}
                            className="text-xs underline text-red-400 hover:text-red-200 transition-colors shrink-0"
                          >
                            Retry
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Access denied */}
                    {PHI_DOWNLOAD_ROLES.includes(role) && attState === 'access-denied' && (
                      <div className="text-sm text-amber-300 bg-amber-900/20 rounded-lg p-3 border border-amber-700/30">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 shrink-0" />
                          <span>AccessDenied: Attachments — your role is not permitted.</span>
                        </div>
                      </div>
                    )}

                    {/* Loaded: list + upload */}
                    {PHI_DOWNLOAD_ROLES.includes(role) && attState === 'loaded' && (
                      <div className="space-y-2">
                        {attachments.length === 0 && (
                          <p className="text-xs text-slate-500 px-1 italic">No attachments yet.</p>
                        )}
                        {attachments.map((att) => {
                          const isSystemGenerated = att.source === 'SYSTEM_GENERATED';
                          const displayName = att.displayLabel ?? att.originalName ?? 'Attachment';
                          return (
                            <div
                              key={att.attachmentId}
                              className={`flex items-center justify-between rounded-lg px-3 py-2 border ${
                                isSystemGenerated
                                  ? 'bg-slate-700/20 border-slate-600/30'
                                  : 'bg-slate-800/40 border-slate-700/30'
                              }`}
                            >
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <p className="text-sm text-slate-200 truncate">{displayName}</p>
                                  {isSystemGenerated && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-600/60 text-slate-400 shrink-0 whitespace-nowrap">
                                      System Generated
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-slate-500 mt-0.5">
                                  {!isSystemGenerated && att.type && <>{att.type} · </>}
                                  {att.mimeType}
                                  {att.sizeBytes != null && ` · ${Math.round(att.sizeBytes / 1024)} KB`}
                                </p>
                              </div>
                              <button
                                onClick={async () => {
                                  try {
                                    await navigateToAttachmentViewer({ referralId, attachmentId: att.attachmentId });
                                  } catch {
                                    // Session creation failed — user can retry
                                  }
                                }}
                                className="ml-3 flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors shrink-0"
                              >
                                <Eye className="w-3 h-3" />
                                View
                              </button>
                            </div>
                          );
                        })}

                        {/* Upload panel — UPLOAD roles only */}
                        {PHI_UPLOAD_ROLES.includes(role) && (
                          <div className="pt-1 space-y-1.5">
                            {/* Document type selector (Step 4) */}
                            <div className="flex items-center gap-2">
                              <label className="text-xs text-slate-500 shrink-0">Type:</label>
                              <select
                                value={uploadType}
                                onChange={(e) => setUploadType(e.target.value)}
                                disabled={uploadState === 'uploading' || uploadState === 'finalizing'}
                                className="flex-1 text-xs px-2 py-1 rounded-lg bg-slate-800 border border-slate-700/50 text-slate-300 focus:outline-none focus:ring-1 focus:ring-slate-600 disabled:opacity-50"
                              >
                                <option value="PDF">PDF / Document</option>
                                <option value="LABS">Labs</option>
                                <option value="IMAGING">Imaging</option>
                                <option value="PHOTO">Photo</option>
                                <option value="OTHER">Other</option>
                              </select>
                            </div>

                            {(uploadState === 'uploading' || uploadState === 'finalizing') ? (
                              <div className="flex items-center gap-2 text-slate-400 text-xs px-1">
                                <Loader2 className="w-3 h-3 animate-spin" />
                                <span>{uploadState === 'uploading' ? 'Uploading…' : 'Finalizing…'}</span>
                              </div>
                            ) : (
                              <button
                                onClick={() => {
                                  setUploadState('idle');
                                  setUploadErr(null);
                                  fileInputRef.current?.click();
                                }}
                                className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                              >
                                + Upload file
                              </button>
                            )}
                            {uploadErr && (
                              <p className="text-xs text-red-400 mt-1">{uploadErr}</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Event history */}
            <div className={activeFocusTarget === 'event-history-section' ? 'rounded-xl transition-all duration-300 learning-focus-highlight-subtle' : ''} data-learning-target="event-history-section">
              <div className="flex items-center gap-2 mb-3">
                <History className="w-3.5 h-3.5 text-slate-500" />
                <SectionTitle>Event History</SectionTitle>
              </div>
              <EventHistory events={events} loading={eventsLoading} workflow={workflow} usersMap={usersMap} />
            </div>

          </div>
        </div>

        {/* Scheduler footer — portal target for SchedulerPanel buttons */}
        {role === 'scheduler' && <div ref={setSchedulerFooterEl} />}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateRequestModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateRequest}
          submitting={modalSubmitting}
        />
      )}
      {showTouchModal && (
        <LogTouchModal
          onClose={() => setShowTouchModal(false)}
          onSubmit={handleLogTouch}
          submitting={modalSubmitting}
        />
      )}

      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

    </>
  );
}
